/**
 * 換算設定の編集モードの状態遷移（プロダクト仕様書「ブラウザでのバリデーション」「エラーの表示」）。
 */

import type { SettingItem } from '@/lib/credit-conversion-settings/tables';

import {
  addedRow,
  checkField,
  clearResolvedDuplicates,
  DATE_FIELD,
  noErrors,
  parseCellKey,
  snapshotOf,
  type EditorErrors,
  type EditorPart,
} from './editorModel';

export interface EditorState {
  item: SettingItem;
  action: 'create' | 'update';
  /** 編集する値の元の適用開始日（追加では null） */
  originalDate: string | null;
  /** 編集する値を取得したときの印（追加では null） */
  version: string | null;
  /** サーバーが最後に求めた当日 */
  today: string;
  date: string;
  /** テーブルごとの行の一覧。並びは `item.tables` の順 */
  parts: EditorPart[];
  /** 消費項目の日本語（値から引く） */
  consumptionLabels: Record<string, string>;
  /** 入力の変更を判定するための、編集モードに入ったときの写し */
  baseline: string;
  errors: EditorErrors;
  /** 値を変更して、まだフォーカスが外れていない入力欄 */
  changed: Record<string, true>;
  /** 増えるたびに、画面で最も上にあるエラーまでスクロールする */
  scrollToError: number;
  /** 追加した行。その行までスクロールし、最初の入力欄にフォーカスを置く */
  focusRowKey: string | null;
}

export type EditorAction =
  | { type: 'change'; key: string; value: string }
  | { type: 'blur'; key: string }
  | { type: 'addRow'; table: string }
  | { type: 'removeRow'; rowKey: string }
  | { type: 'showErrors'; errors: EditorErrors }
  | { type: 'focusedRow' };

export function openEditor(
  init: Pick<
    EditorState,
    | 'item'
    | 'action'
    | 'originalDate'
    | 'version'
    | 'today'
    | 'date'
    | 'parts'
    | 'consumptionLabels'
  >,
): EditorState {
  return {
    ...init,
    baseline: snapshotOf(init.date, init.parts),
    errors: noErrors,
    changed: {},
    scrollToError: 0,
    focusRowKey: null,
  };
}

export function isDirty(state: EditorState): boolean {
  return snapshotOf(state.date, state.parts) !== state.baseline;
}

function withFieldError(
  fields: Record<string, string>,
  key: string,
  error: string | null,
): Record<string, string> {
  const next = { ...fields };
  if (error) next[key] = error;
  else delete next[key];
  return next;
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'change': {
      const { key, value } = action;
      const cell = key === DATE_FIELD ? null : parseCellKey(key);
      const parts = cell
        ? state.parts.map((part) => ({
            ...part,
            rows: part.rows.map((row) =>
              row.key === cell.rowKey
                ? { ...row, values: { ...row.values, [cell.column]: value } }
                : row,
            ),
          }))
        : state.parts;
      const date = key === DATE_FIELD ? value : state.date;
      let { fields, rows: rowErrors } = state.errors;
      // エラーのある入力欄は値が変わるたびにチェックをやり直す（サーバーだけで行うチェックのエラーはここで消える）
      if (key in fields) {
        fields = withFieldError(fields, key, checkField(parts, key, value, state.today));
      }
      const table = cell
        ? parts.find((p) => p.rows.some((r) => r.key === cell.rowKey))?.table
        : null;
      const axis = table?.hasAxis ? table.columns[0].name : null;
      if (axis && cell?.column === axis && Object.keys(rowErrors).length > 0) {
        rowErrors = clearResolvedDuplicates(parts, rowErrors);
      }
      return {
        ...state,
        date,
        parts,
        errors: { ...state.errors, fields, rows: rowErrors },
        changed: { ...state.changed, [key]: true },
      };
    }
    case 'blur': {
      if (!state.changed[action.key]) return state;
      const cell = action.key === DATE_FIELD ? null : parseCellKey(action.key);
      const value = cell
        ? (state.parts.flatMap((p) => p.rows).find((r) => r.key === cell.rowKey)?.values[
            cell.column
          ] ?? '')
        : state.date;
      const changed = { ...state.changed };
      delete changed[action.key];
      return {
        ...state,
        changed,
        errors: {
          ...state.errors,
          fields: withFieldError(
            state.errors.fields,
            action.key,
            checkField(state.parts, action.key, value, state.today),
          ),
        },
      };
    }
    case 'addRow': {
      const part = state.parts.find((p) => p.table.name === action.table);
      if (!part) return state;
      const row = addedRow(part.table);
      const parts = state.parts.map((p) => (p === part ? { ...p, rows: [...p.rows, row] } : p));
      return { ...state, parts, focusRowKey: row.key };
    }
    case 'removeRow': {
      const parts = state.parts.map((p) => ({
        ...p,
        rows: p.rows.filter((r) => r.key !== action.rowKey),
      }));
      const prefix = `${action.rowKey}/`;
      const fields = Object.fromEntries(
        Object.entries(state.errors.fields).filter(([key]) => !key.startsWith(prefix)),
      );
      return {
        ...state,
        parts,
        errors: {
          ...state.errors,
          fields,
          rows: clearResolvedDuplicates(parts, state.errors.rows),
        },
      };
    }
    case 'showErrors':
      return { ...state, errors: action.errors, scrollToError: state.scrollToError + 1 };
    case 'focusedRow':
      return { ...state, focusRowKey: null };
  }
}
